import React from "react";
import { useT } from "@/i18n";

// Top-view car diagram (front at top). Zones colored by damage severity.
// value: array of { key, severity }. onToggle(key) adds/removes a zone.
const ZONES = [
  { key: "front_left",  x: 26,  y: 20,  w: 52, h: 104, rx: 22 },
  { key: "front",       x: 84,  y: 20,  w: 52, h: 104, rx: 12 },
  { key: "front_right", x: 142, y: 20,  w: 52, h: 104, rx: 22 },
  { key: "left",        x: 26,  y: 132, w: 52, h: 116, rx: 10 },
  { key: "roof",        x: 84,  y: 132, w: 52, h: 116, rx: 10 },
  { key: "right",       x: 142, y: 132, w: 52, h: 116, rx: 10 },
  { key: "rear_left",   x: 26,  y: 256, w: 52, h: 104, rx: 22 },
  { key: "rear",        x: 84,  y: 256, w: 52, h: 104, rx: 12 },
  { key: "rear_right",  x: 142, y: 256, w: 52, h: 104, rx: 22 },
];

export const SEVERITY_COLORS = { light: "#f59e0b", medium: "#f97316", severe: "#dc2626" };

// Accept legacy string zones or rich objects → always [{key, severity, photos}].
export const normalizeZones = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((z) => (typeof z === "string"
      ? { key: z, severity: "medium", photos: [] }
      : { key: z.key, severity: z.severity || "medium", photos: z.photos || [] }))
    .filter((z) => z.key);

export default function CarDamageSchema({ value = [], onToggle, readOnly = false }) {
  const { t } = useT();
  const map = new Map((value || []).map((z) => [z.key, z.severity || "medium"]));

  return (
    <div className="relative mx-auto w-[160px] shrink-0" data-testid="damage-schema">
      <svg viewBox="0 0 220 380" className="w-full select-none" role="img" aria-label={t("damage.schema.title")}>
        {/* car silhouette (adapts to light/dark) */}
        <rect x="14" y="8" width="192" height="364" rx="56"
          className="fill-slate-100 stroke-slate-300 dark:fill-white/[0.04] dark:stroke-white/20" strokeWidth="2" />
        {/* windshield / rear window */}
        <path d="M64 118 L156 118 L140 150 L80 150 Z" className="fill-sky-200/70 dark:fill-sky-400/10" />
        <path d="M80 250 L140 250 L156 282 L64 282 Z" className="fill-sky-200/70 dark:fill-sky-400/10" />
        {/* wheels */}
        {[[6, 70], [200, 70], [6, 262], [200, 262]].map(([wx, wy], i) => (
          <rect key={i} x={wx} y={wy} width="14" height="48" rx="6" className="fill-slate-400 dark:fill-white/20" />
        ))}
        {/* zones */}
        {ZONES.map((z) => {
          const sev = map.get(z.key);
          const active = !!sev;
          return (
            <g key={z.key} onClick={() => !readOnly && onToggle?.(z.key)}
              style={{ cursor: readOnly ? "default" : "pointer" }} data-testid={`damage-zone-${z.key}`}>
              <title>{t(`damage.zone.${z.key}`)}</title>
              <rect
                x={z.x} y={z.y} width={z.w} height={z.h} rx={z.rx}
                fill={active ? SEVERITY_COLORS[sev] : undefined}
                stroke={active ? SEVERITY_COLORS[sev] : undefined}
                className={active
                  ? "transition-all duration-150"
                  : "fill-slate-200/80 stroke-slate-400 transition-all duration-150 hover:fill-amber-200 dark:fill-white/[0.07] dark:stroke-white/30"}
                strokeWidth={active ? 2.5 : 1.5}
                fillOpacity={active ? 0.9 : undefined}
              />
              {active && (
                <path
                  d={`M${z.x + z.w / 2 - 6} ${z.y + z.h / 2} l4 4 l8 -9`}
                  fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
                />
              )}
            </g>
          );
        })}
      </svg>
      <span className="absolute left-1/2 top-[-8px] -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-white/40">
        {t("damage.schema.front")}
      </span>
    </div>
  );
}
